import {
  N0PassengerSsrsUpdate,
  N0SsrPassengerUpdateRequest,
} from '@models/requests/ssr-passenger-update-request.model';
import { createSelector } from '@ngrx/store';
import { selectBagsFeatureState } from '@reducers';
import { convertBookingBagsToSsrUpdate } from '@utils/ssrs.utils';
import {
  selectBookingBagsTotalCost,
  selectBookingBags,
  selectBookingSsrAvailability,
  selectBookingPaidBagsTotalCost,
} from './booking.selector';
import {selectIsPaymentsPage} from "@selectors/router.selector";

/* istanbul ignore next  */
export const selectBagsTotalSelectionsState = createSelector(
  selectBagsFeatureState,
  (state) => state.totalSelections,
);

export const selectHighestBagsByPassenger = createSelector(
  selectBagsTotalSelectionsState,
  (selections) => {
    if (!selections || selections.length === 0) return null;

    let passengers: N0PassengerSsrsUpdate[] = [];

    selections.forEach((s) => {
      if (passengers.length === 0) {
        passengers = s.passengers;
        return;
      }

      passengers = passengers.map((passenger) => {
        const pSelection = s.passengers.find(
          (p) => p.passengerKey === passenger.passengerKey,
        );

        let ssrs = passenger.ssrs;
        pSelection?.ssrs.forEach((ssr) => {
          const currentSsr = ssrs.find((ps) => ssr.ssrCode === ps.ssrCode);

          if (!currentSsr) {
            ssrs = ssrs.concat(ssr);
          } else {
            if (ssr.count > currentSsr.count) {
              ssrs = ssrs.map((ps) => {
                return ssr.ssrCode === ps.ssrCode ? ssr : ps;
              });
            }
          }
        });

        return {
          ...passenger,
          ssrs: ssrs,
        };
      });
    });

    return passengers;
  },
);

export const selectHighestIncludedOrPaidBagsByPassenger = createSelector(
  selectBookingBags,
  (bookingBags) => {
    if (!bookingBags || bookingBags.length === 0) return null;

    let passengers: N0PassengerSsrsUpdate[] = [];

    bookingBags.forEach((j) => {
      const journeyBags = convertBookingBagsToSsrUpdate(j);
      if (passengers.length === 0) {
        passengers = journeyBags.passengers;
        return;
      }

      passengers = passengers.map((passenger) => {
        const pBags = journeyBags.passengers.find(
          (p) => p.passengerKey === passenger.passengerKey,
        );

        if (!pBags) {
          return { ...passenger };
        }

        return {
          ...passenger,
          ssrs: pBags.ssrs.map((pb) => {
            const bags = passenger.ssrs.find(
              (ssr) => ssr.ssrCode === pb.ssrCode,
            );

            return bags && bags.count > pb.count ? bags : pb;
          }),
        };
      });
    });

    return passengers;
  },
);

export const selectIncludedOrPaidBagsByJourney = createSelector(
  selectBookingBags,
  (bookingBags) => {
    if (!bookingBags || bookingBags.length === 0) return null;

    let journeys: N0SsrPassengerUpdateRequest[] = [];

    bookingBags.forEach((j) => {
      const journeyBags = convertBookingBagsToSsrUpdate(j);
      journeys = journeys.concat(journeyBags);
    });

    return journeys;
  },
);

export const selectBagsTotalPriceTripSummary = createSelector(
  selectBagsTotalSelectionsState,
  selectBookingSsrAvailability,
  selectIncludedOrPaidBagsByJourney,
  selectBookingBagsTotalCost,
  selectBookingPaidBagsTotalCost,
  (
    selections,
    ssrAvailability,
    includedOrPaidBags,
    bookingBagsTotalCost,
    paidBagsTotalCost,
  ) => {
    let totalPrice = 0;

    if (!selections) {
      return bookingBagsTotalCost;
    }

    selections.forEach((s) => {
      s.passengers.forEach((p) => {
        p.ssrs.forEach((ssr) => {
          if (ssr.count > 0) {
            const count = ssr.count -
              (includedOrPaidBags
                ?.find((j) => j.journeyKey === s.journeyKey)
                ?.passengers.find(
                  (pass) => pass.passengerKey === p.passengerKey,
                )
                ?.ssrs.find(
                  (includedSsr) => includedSsr.ssrCode === ssr.ssrCode,
                )?.count ?? 0);

            const price = ssrAvailability?.journeySsrs
              .find((j) => j.journeyKey === s.journeyKey)
              ?.bagSsrs.find((bagSsr) => bagSsr.ssrCode === ssr.ssrCode)
              ?.passengersAvailability.find(
                (pa) => pa.passengerKey === p.passengerKey,
              )?.price ?? 0;

            totalPrice += count * price;
          }
        });
      });
    });

    totalPrice += paidBagsTotalCost;

    return totalPrice;
  },
);

export const selectBagsTotalPriceTripSummaryRounded = createSelector(
  selectBagsTotalPriceTripSummary,
  Math.ceil,
);
